<script setup lang="ts">
  import { SmsTemplateQueryParams } from '@api/query';
  import { useQueryClient } from '@tanstack/vue-query';
  import { snakeCase } from 'change-case';
  import { computed, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import type { SMSTemplateSchema } from '@stuut/api-sdk/models';
  import { toast } from '@ui/components/sonner';
  import { ModelTable, TablePagination } from '@ui/components/table';

  import { usePaginatedTable, usePersistentSelection } from '@/composables';
  import { sdk } from '@/sdk';
  import { useProfile } from '@/store';
  import useSmsTemplates from '@/store/smsTemplates';

  import { SmsTemplateColumns } from './columns';
  import SmsTemplateTableToolbar from './SmsTemplateTableToolbar.vue';

  const emits = defineEmits<{
    (e: 'templateSelected', template: SMSTemplateSchema): void;
  }>();

  const route = useRoute();
  const queryClient = useQueryClient();

  const queryParams = ref<SmsTemplateQueryParams>(
    SmsTemplateQueryParams.init({
      defaults: {
        page: 0,
        pageSize: 50,
        sortBy: 'name',
        sortOrder: 'asc',
      },
      json: route.query,
    }),
  );

  const profileStore = useProfile();
  const profileExists = computed(() => Boolean(profileStore.profile.id));

  const smsTemplateStore = useSmsTemplates();

  const { clearSelection, selectedItems, updateSelection } =
    usePersistentSelection<SMSTemplateSchema>((id: string) => smsTemplateStore.getById(id));

  const loading = ref(false);

  const paramsForKey = computed(() => {
    const raw = queryParams.value.serialize();
    return JSON.parse(JSON.stringify(raw));
  });

  const queryKey = computed(() => ['smsTemplates', paramsForKey.value]);

  const { table } = usePaginatedTable<SMSTemplateSchema, SmsTemplateQueryParams>({
    params: queryParams,
    queryOptions: {
      enabled: profileExists,
      initialData: [],
      queryFn: async () => {
        loading.value = true;
        try {
          const sortBy = queryParams.value.sortBy ? snakeCase(queryParams.value.sortBy) : undefined;

          const response = await sdk.smsTemplates.list({
            limit: queryParams.value.pageSize,
            page: queryParams.value.page,
            search: queryParams.value.search || undefined,
            sortBy: sortBy as any,
            sortOrder: queryParams.value.sortOrder as any,
          });

          smsTemplateStore.$patch({
            templates: response.data || [],
            totalTemplates: response.totalRecords ?? 0,
          });

          return response.data || [];
        } catch (e) {
          console.error(e);
          toast.error('Failed to load SMS templates');
          return [];
        } finally {
          loading.value = false;
        }
      },
      queryKey,
    },
    selectItemsWithIds: updateSelection,
    tableOptions: {
      get columns() {
        return SmsTemplateColumns;
      },
      getRowId: (row) => row.id,
    },
    useRouterQuery: true,
  });

  const isCreatingTemplate = ref(false);
  const createTemplate = async () => {
    isCreatingTemplate.value = true;
    try {
      const newTemplate = await sdk.smsTemplates.create({
        body: '',
        name: 'New SMS Template',
      });

      smsTemplateStore.addTemplatesToState([newTemplate]);
      queryClient.invalidateQueries({ queryKey: ['smsTemplates'] });

      emits('templateSelected', newTemplate);
    } catch (error) {
      console.error(error);
      toast.error('Failed to create template');
    } finally {
      isCreatingTemplate.value = false;
    }
  };

  const isDeletingTemplates = ref(false);
  const deleteSelectedTemplates = async () => {
    isDeletingTemplates.value = true;
    try {
      const selectedTemplates = selectedItems.value;

      await Promise.all(
        selectedTemplates.map((template) =>
          sdk.smsTemplates.delete({ smsTemplateId: template.id }),
        ),
      );

      smsTemplateStore.removeTemplatesFromState(selectedTemplates);
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ['smsTemplates'] });
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete templates');
    } finally {
      isDeletingTemplates.value = false;
    }
  };

  watch(paramsForKey, () => {
    queryClient.invalidateQueries({ queryKey: ['smsTemplates'] });
  });

  const updateItemInCache = (item: SMSTemplateSchema) => {
    queryClient.setQueriesData<SMSTemplateSchema[]>({ queryKey: ['smsTemplates'] }, (old) =>
      Array.isArray(old) ? old.map((i) => (i.id === item.id ? item : i)) : old,
    );
    smsTemplateStore.updateTemplatesInState([item]);
  };

  const removeItemFromCache = (item: SMSTemplateSchema) => {
    queryClient.setQueriesData<SMSTemplateSchema[]>({ queryKey: ['smsTemplates'] }, (old) =>
      Array.isArray(old) ? old.filter((i) => i.id !== item.id) : old,
    );
    smsTemplateStore.removeTemplatesFromState([item]);
  };

  defineExpose({
    removeItemFromCache,
    updateItemInCache,
  });
</script>

<template>
  <div class="grid grid-rows-[min-content_1fr_min-content] overflow-x-auto h-full gap-y-3">
    <div class="flex gap-4">
      <SmsTemplateTableToolbar
        v-model="queryParams"
        :selected-templates="selectedItems"
        :is-creating-template="isCreatingTemplate"
        :is-deleting-templates="isDeletingTemplates"
        @create-template="createTemplate"
        @delete-selected-templates="deleteSelectedTemplates"
      />
    </div>

    <ModelTable
      :table="table"
      :columns="SmsTemplateColumns"
      :loading="loading"
      :no-search-result="!table.getRowModel().rows?.length"
      v-bind="$attrs"
      @cell-click="(cell) => emits('templateSelected', cell.row.original)"
    />

    <TablePagination
      :table
      :total-records="smsTemplateStore.totalTemplates"
      :loading="loading"
      :global-selection="selectedItems"
      @clear-selection="clearSelection"
    />
  </div>
</template>
